import { create } from 'zustand'
import { assertWorkspaceEditable, runWorkspaceActivity } from '../services/workspaceLock'
import { assertLocalSaveComplete, flushAutosave } from '../services/autosave'
import { applyCommand, applyCommands, calculateMetrics, validateProject } from '../domain/commands'
import { ensureStarterGarden } from '../domain/gardenFixtures'
import { ensureStarterOrchard } from '../domain/orchard'
import { applyModernBarnPreset, isModernBarnPreset } from '../domain/presets'
import { slugify } from '../domain/refs'
import { modernBarnProject } from '../domain/sampleProject'
import { createReferenceHouse, REFERENCE_HOUSE_REF } from '../domain/referenceHouse'
import { fitZielonkiInterior, hasZielonkiInterior, upgradeZielonkiRoof, upgradeZielonkiGlazing, upgradeZielonkiTerrace, upgradeZielonkiLivingWall } from '../domain/zielonkiInterior'
import { upgradeZielonkiPlacement } from '../domain/zielonkiPlacement'
import { REFERENCE_YEAR, type SunTime } from '../domain/solar'
import type { SunlightAnalysis } from '../domain/sunlight'
import { createTerrainProject, isZielonkiProject, type TerrainInput } from '../domain/terrain'
import { listWorkspaces, loadWorkspace, loadEditHistory, normalizeWorkspaceName, renameWorkspace, saveWorkspace, type WorkspaceSummary, type LocalEditHistory } from '../services/persistence'
import { stableJson } from '../domain/workspaceSync'
import { ARCHITEKT_REF, synchronizePublishedArchitekt } from '../services/publishedArchitekt'
import { NOWE_MPZP_REF, synchronizePublishedNoweMpzp } from '../services/publishedNoweMpzp'
import { NOWE_MPZP_V2_REF, synchronizePublishedNoweMpzpV2 } from '../services/publishedNoweMpzpV2'
import type { DraftChangeSetModel, HeightMeasureKind, PersistedWorkspace, ProjectCommand, ProjectV2, ProposalRecord, StructureReport, TransformMode, VariantModel, ViewerMode } from '../domain/types'

interface StudioState {
  transparencyMode: boolean
  transparentRefs: string[]
  setTransparencyMode: (enabled: boolean) => void
  makeTransparent: (ref: string) => void
  resetTransparency: () => void
  measurementPoints: Array<{ x: number; y: number; z: number }>
  setMeasurementPoints: (points: Array<{ x: number; y: number; z: number }>) => void
  project: ProjectV2
  history: ProjectV2[]
  future: ProjectV2[]
  variants: VariantModel[]
  proposals: ProposalRecord[]
  draftChangeSets: DraftChangeSetModel[]
  selectedRef: string | null
  repositioningRef: string | null
  transformMode: TransformMode
  viewerMode: ViewerMode
  heightMeasureKind: HeightMeasureKind
  activePlanStoreyRef: string | null
  month: number
  sunTime: SunTime
  sunAnimation: 'none' | 'day' | 'year'
  sunOverlay: { enabled: boolean; targetRef: string | null; result: SunlightAnalysis | null }
  explodeStoreys: boolean
  planningOverlayVisible: boolean
  setPlanningOverlayVisible: (value: boolean) => void
  treesVisible: boolean
  setTreesVisible: (value: boolean) => void
  neighborsVisible: boolean
  neighborViewRequest: { sequence: number; ref: string; eyeHeightM: number }
  setNeighborsVisible: (value: boolean) => void
  viewFromNeighbor: (ref: string, eyeHeightM: number) => void
  webMcpAvailable: boolean
  texturesReady: boolean
  hydrated: boolean
  /** The start screen; open until a project has been chosen, and again on demand from the Projects button. */
  launcherOpen: boolean
  savedWorkspaces: WorkspaceSummary[]
  projectSyncConflicts: string[]
  loadingWorkspaces: boolean
  confirmationVariantRef: string | null
  structureReport: StructureReport | null
  toast: string | null
  helpOpen: boolean
  cameraRefocusRequest: number
  gardenFocusRequest: { sequence: number; targetX: number; targetZ: number }
  setSelectedRef: (ref: string | null) => void
  setTransformMode: (mode: TransformMode) => void
  setViewerMode: (mode: ViewerMode) => void
  setHeightMeasureKind: (kind: HeightMeasureKind) => void
  setActivePlanStoreyRef: (ref: string | null) => void
  setMonth: (month: number) => void
  setSunTime: (time: Partial<SunTime>) => void
  setSunAnimation: (mode: 'none' | 'day' | 'year') => void
  setSunOverlay: (overlay: Partial<{ enabled: boolean; targetRef: string | null; result: SunlightAnalysis | null }>) => void
  setExplodeStoreys: (value: boolean) => void
  setWebMcpAvailable: (value: boolean) => void
  setTexturesReady: (value: boolean) => void
  setHydrated: (value: boolean) => void
  setConfirmationVariantRef: (ref: string | null) => void
  setStructureReport: (report: StructureReport | null) => void
  setToast: (message: string | null) => void
  setHelpOpen: (value: boolean) => void
  refocusCamera: () => void
  focusGardenFixtures: (fixtureRef?: string) => void
  useModernBarnPreset: () => ProjectV2
  replaceProject: (project: ProjectV2) => void
  restoreWorkspace: (workspace: PersistedWorkspace, editHistory?: LocalEditHistory) => void
  openLauncher: () => Promise<void>
  closeLauncher: () => void
  startTerrain: (input: TerrainInput) => ProjectV2
  loadBundledStudy: () => void
  openZielonkiStudy: () => Promise<void>
  fitReferenceToZielonki: () => Promise<void>
  openReferenceHouse: () => Promise<void>
  openWorkspace: (ref: string) => Promise<void>
  renameWorkspace: (ref: string, name: string) => Promise<void>
  createVariant: (label: string, commands: ProjectCommand[], metadata?: Pick<ProposalRecord, 'sourceChangeSetRef' | 'recreatedFromRef'>) => VariantModel
  applyVariant: (ref: string) => ProjectV2
  discardVariant: (ref: string, reason?: string) => void
  reopenProposal: (ref: string) => VariantModel
  recreateProposal: (ref: string) => VariantModel
  createDraftChangeSet: (ref: string, label: string, baseRevision: number) => DraftChangeSetModel
  addDraftOperations: (ref: string, operations: ProjectCommand[]) => { draft: DraftChangeSetModel; preview: ProjectV2 }
  finalizeDraftChangeSet: (ref: string) => VariantModel
  discardDraftChangeSet: (ref: string) => void
  beginReposition: (ref: string) => void
  endReposition: () => void
  commitCommand: (command: ProjectCommand) => void
  commitCommands: (commands: ProjectCommand[], message?: string) => ProjectV2
  undo: () => ProjectV2
  redo: () => ProjectV2
}

let variantSequence = 0
const revokeReport = (report: StructureReport | null) => report?.views.forEach((view) => URL.revokeObjectURL(view.imageUrl))
const daysInMonth = (month: number) => new Date(Date.UTC(REFERENCE_YEAR, month, 0)).getUTCDate()
const clampSunTime = (time: SunTime): SunTime => {
  const month = Math.min(12, Math.max(1, Math.round(time.month)))
  return { month, day: Math.min(daysInMonth(month), Math.max(1, Math.round(time.day))), hour: Math.min(24, Math.max(0, time.hour)) }
}
const commandFocusRef = (commands: ProjectCommand[]) => {
  for (const command of commands) {
    for (const [key, value] of Object.entries(command)) if ((key === 'segmentRef' || key.endsWith('Ref')) && typeof value === 'string') return value
  }
  return null
}
const staleRecords = (records: ProposalRecord[], revision: number) => records.map((proposal) => proposal.status === 'pending' && proposal.baseRevision !== revision ? { ...proposal, status: 'stale' as const } : proposal)
const staleDrafts = (drafts: DraftChangeSetModel[], revision: number) => drafts.map((draft) => draft.baseRevision === revision ? draft : { ...draft, status: 'stale' as const })

export const useStudioStore = create<StudioState>((rawSet, get) => {
  const set: typeof rawSet = (update) => {
    const next = typeof update === 'function' ? update(get()) : update
    if ('project' in next || 'proposals' in next || 'draftChangeSets' in next || 'launcherOpen' in next) assertWorkspaceEditable()
    if (next.project && next.project.ref !== get().project.ref) assertLocalSaveComplete()
    // A discarded/staled preview must never leave the inspector pointing at nothing.
    if (next.variants && !next.variants.some(item => item.ref === (next.confirmationVariantRef ?? get().confirmationVariantRef))) next.confirmationVariantRef = null
    rawSet(next)
  }
  return ({
  transparencyMode: false, transparentRefs: [],
  setTransparencyMode: (transparencyMode) => set(transparencyMode
    ? { transparencyMode, viewerMode: 'edit', activePlanStoreyRef: null, repositioningRef: null, selectedRef: null, toast: 'Click objects to make them transparent. Reset restores all objects. Esc finishes.' }
    : { transparencyMode }),
  makeTransparent: (ref) => set((state) => ({ transparentRefs: state.transparentRefs.includes(ref) ? state.transparentRefs : [...state.transparentRefs, ref] })),
  resetTransparency: () => set({ transparentRefs: [], transparencyMode: false }),
  measurementPoints: [], setMeasurementPoints: (measurementPoints) => set({ measurementPoints }),
  project: structuredClone(modernBarnProject), history: [], future: [], variants: [], proposals: [], draftChangeSets: [], selectedRef: null, repositioningRef: null,
  transformMode: 'translate', viewerMode: 'edit', heightMeasureKind: 'auto', activePlanStoreyRef: null, month: 7,
  sunTime: { month: 7, day: 15, hour: 14 }, sunAnimation: 'none', sunOverlay: { enabled: false, targetRef: null, result: null },
  planningOverlayVisible: false,
  setPlanningOverlayVisible: (planningOverlayVisible) => set({ planningOverlayVisible }),
  treesVisible: true,
  setTreesVisible: (treesVisible) => set({ treesVisible }),
  neighborsVisible: false, neighborViewRequest: { sequence: 0, ref: '', eyeHeightM: 1.6 },
  setNeighborsVisible: (neighborsVisible) => set((state) => ({ neighborsVisible, sunOverlay: { ...state.sunOverlay, result: null } })),
  viewFromNeighbor: (ref, eyeHeightM) => set((state) => ({ neighborsVisible: true, viewerMode: 'edit', activePlanStoreyRef: null, neighborViewRequest: { sequence: state.neighborViewRequest.sequence + 1, ref, eyeHeightM }, toast: 'Approximate facade viewpoint; neighboring window locations are not surveyed.' })),
  explodeStoreys: false, webMcpAvailable: false, texturesReady: false, hydrated: false, launcherOpen: true, savedWorkspaces: [], projectSyncConflicts: [], loadingWorkspaces: true, confirmationVariantRef: null, structureReport: null,
  toast: 'Loaded the ProjectV2 Zielonki spatial model.', helpOpen: false, cameraRefocusRequest: 0, gardenFocusRequest: { sequence: 0, targetX: 0, targetZ: 0 },
  setSelectedRef: (selectedRef) => set({ selectedRef }),
  setTransformMode: (transformMode) => set({ transformMode }),
  setViewerMode: (viewerMode) => set({ viewerMode, transparencyMode: false, activePlanStoreyRef: viewerMode === 'plan' ? get().activePlanStoreyRef : null }),
  setHeightMeasureKind: (heightMeasureKind) => set({ heightMeasureKind }),
  setActivePlanStoreyRef: (activePlanStoreyRef) => set({ activePlanStoreyRef, viewerMode: activePlanStoreyRef ? 'plan' : 'edit' }),
  setMonth: (month) => set((state) => { const time = clampSunTime({ ...state.sunTime, month, day: 15 }); return { month: time.month, sunTime: time } }),
  setSunTime: (time) => set((state) => { const next = clampSunTime({ ...state.sunTime, ...time }); return { sunTime: next, month: next.month } }),
  setSunAnimation: (sunAnimation) => set({ sunAnimation }),
  setSunOverlay: (overlay) => set((state) => ({ sunOverlay: { ...state.sunOverlay, ...overlay } })),
  setExplodeStoreys: (explodeStoreys) => set({ explodeStoreys }),
  setWebMcpAvailable: (webMcpAvailable) => set({ webMcpAvailable }),
  setTexturesReady: (texturesReady) => set({ texturesReady }),
  setHydrated: (hydrated) => set({ hydrated }),
  setConfirmationVariantRef: (confirmationVariantRef) => set({ confirmationVariantRef }),
  setStructureReport: (structureReport) => { revokeReport(get().structureReport); set({ structureReport }) },
  setToast: (toast) => set({ toast }),
  setHelpOpen: (helpOpen) => set({ helpOpen }),
  beginReposition: (repositioningRef) => set({ repositioningRef, transparencyMode: false, viewerMode: 'edit', transformMode: 'translate', toast: 'Drag the selected object to its new position.' }),
  endReposition: () => set({ repositioningRef: null }),
  refocusCamera: () => set((state) => ({
    cameraRefocusRequest: state.cameraRefocusRequest + 1,
    viewerMode: 'edit',
    activePlanStoreyRef: null,
    toast: state.project.buildings[0] ? `Camera refocused on ${state.project.buildings[0].name}.` : 'Camera refocused on the site.',
  })),
  focusGardenFixtures: (fixtureRef) => set((state) => {
    const fixtures = fixtureRef ? state.project.landscape.fixtures.filter((fixture) => fixture.ref === fixtureRef) : state.project.landscape.fixtures
    if (!fixtures.length) return { toast: fixtureRef ? `Fixture not found: ${fixtureRef}.` : 'Place a garden fixture first.' }
    const targetX = fixtures.reduce((sum, fixture) => sum + fixture.position.x, 0) / fixtures.length
    const targetZ = fixtures.reduce((sum, fixture) => sum + fixture.position.z, 0) / fixtures.length
    return { gardenFocusRequest: { sequence: state.gardenFocusRequest.sequence + 1, targetX, targetZ }, viewerMode: 'edit', activePlanStoreyRef: null, toast: fixtureRef ? `Camera focused on ${fixtures[0].name}.` : 'Camera focused on the placed garden fixtures.' }
  }),
  useModernBarnPreset: () => {
    const state = get()
    if (isModernBarnPreset(state.project)) {
      set({ selectedRef: state.project.buildings.find((item) => item.kind === 'house')?.ref ?? null, cameraRefocusRequest: state.cameraRefocusRequest + 1, toast: 'Modern barn preset is already active.' })
      return state.project
    }
    const next = applyModernBarnPreset(state.project)
    next.revision = state.project.revision + 1
    next.updatedAt = new Date().toISOString()
    const blocking = validateProject(next).filter((issue) => issue.severity === 'error')
    if (blocking.length) throw new Error(blocking[0].message)
    const houseRef = next.buildings.find((item) => item.kind === 'house')?.ref ?? null
    set({ project: next, future: [], history: [...state.history, structuredClone(state.project)].slice(-40), variants: [], proposals: staleRecords(state.proposals, next.revision), draftChangeSets: staleDrafts(state.draftChangeSets, next.revision), selectedRef: houseRef, cameraRefocusRequest: state.cameraRefocusRequest + 1, toast: 'Modern barn preset applied: two levels and a 45° gable.' })
    return next
  },
  replaceProject: (project) => { revokeReport(get().structureReport); set((state) => ({ project, transparencyMode: false, transparentRefs: [], variants: [], proposals: [], draftChangeSets: [], history: [], future: [], structureReport: null, sunOverlay: { ...state.sunOverlay, result: null }, toast: `Loaded ${project.name}.` })) },
  restoreWorkspace: (workspace, editHistory) => {
    const savedProject = stableJson(workspace.project)
    get().resetTransparency()
    // Includes saved copies with different project refs; only mapped survey inventories need correction.
    if (workspace.project.landscape.plants.some((plant) => plant.surveyHandle)) {
      workspace = { ...workspace, project: ensureStarterOrchard(workspace.project) }
    }
    workspace = { ...workspace, project: upgradeZielonkiPlacement(upgradeZielonkiLivingWall(upgradeZielonkiTerrace(upgradeZielonkiGlazing(upgradeZielonkiRoof(workspace.project))))) }
    revokeReport(get().structureReport)
    const proposals = staleRecords(workspace.proposals, workspace.project.revision).map((proposal) =>
      proposal.status === 'pending' ? { ...proposal, issues: validateProject(proposal.project) } : proposal)
    const variants = proposals.filter((proposal) => proposal.status === 'pending')
    const history = savedProject === stableJson(workspace.project) ? editHistory : undefined
    set({ project: workspace.project, proposals, variants, draftChangeSets: staleDrafts(workspace.draftChangeSets, workspace.project.revision), history: history?.history ?? [], future: history?.future ?? [], confirmationVariantRef: null, structureReport: null, sunOverlay: { enabled: false, targetRef: null, result: null }, toast: `Loaded ${workspace.project.name} with ${proposals.length} proposal record${proposals.length === 1 ? '' : 's'}.` })
  },
  openLauncher: async () => {
    assertWorkspaceEditable()
    await flushAutosave()
    set({ launcherOpen: true, loadingWorkspaces: true })
    try {
      set({ projectSyncConflicts: [] })
      const architektConflicts = await synchronizePublishedArchitekt()
      if (architektConflicts.length) set((state) => ({ projectSyncConflicts: [...state.projectSyncConflicts, ...architektConflicts] }))
      const noweMpzpConflicts = await synchronizePublishedNoweMpzp()
      if (noweMpzpConflicts.length) set((state) => ({ projectSyncConflicts: [...state.projectSyncConflicts, ...noweMpzpConflicts] }))
      const noweMpzpV2Conflicts = await synchronizePublishedNoweMpzpV2()
      if (noweMpzpV2Conflicts.length) set((state) => ({ projectSyncConflicts: [...state.projectSyncConflicts, ...noweMpzpV2Conflicts] }))
      set({ savedWorkspaces: await listWorkspaces({ includeArchived: false }) })
    }
    catch (error) { set({ savedWorkspaces: [], toast: `Saved projects could not be read: ${error instanceof Error ? error.message : 'storage unavailable'}.` }) }
    finally { set({ loadingWorkspaces: false }) }
  },
  closeLauncher: () => set({ launcherOpen: false }),
  startTerrain: (input) => {
    let project: ProjectV2
    try { project = createTerrainProject(input) }
    catch (error) { throw new Error(error instanceof Error && 'issues' in error ? ((error as { issues: Array<{ message: string }> }).issues[0]?.message ?? 'Check the plot values.') : error instanceof Error ? error.message : 'Check the plot values.') }
    get().replaceProject(project)
    set((state) => ({ launcherOpen: false, hydrated: true, selectedRef: null, cameraRefocusRequest: state.cameraRefocusRequest + 1, toast: `${project.name} created: ${input.widthM} × ${input.depthM} m plot, north ${input.northDegrees}°. Add a house from the inspector.` }))
    return project
  },
  loadBundledStudy: () => {
    const project = ensureStarterOrchard(ensureStarterGarden(structuredClone(modernBarnProject)))
    get().replaceProject(project)
    set((state) => ({ launcherOpen: false, hydrated: true, selectedRef: null, cameraRefocusRequest: state.cameraRefocusRequest + 1, toast: 'Loaded the Zielonki house study.' }))
  },
  openReferenceHouse: async () => {
    assertWorkspaceEditable()
    await flushAutosave()
    try {
      const saved = await loadWorkspace(REFERENCE_HOUSE_REF)
      if (saved) get().restoreWorkspace(saved, await loadEditHistory(saved.project))
      else get().replaceProject(createReferenceHouse())
      set((state) => ({ launcherOpen: false, hydrated: true, selectedRef: null, viewerMode: 'edit', activePlanStoreyRef: null, explodeStoreys: false, cameraRefocusRequest: state.cameraRefocusRequest + 1, toast: 'Reference house loaded. Open House interior to explore both floors.' }))
    } catch (error) { set({ toast: `Could not open reference house: ${error instanceof Error ? error.message : 'storage unavailable'}.` }) }
  },
  openZielonkiStudy: async () => {
    assertWorkspaceEditable()
    await flushAutosave()
    try {
      const saved = await loadWorkspace(modernBarnProject.ref)
      if (saved) await get().openWorkspace(saved.project.ref)
      else {
        const project = upgradeZielonkiPlacement(fitZielonkiInterior(ensureStarterOrchard(ensureStarterGarden(structuredClone(modernBarnProject))), createReferenceHouse()))
        await saveWorkspace({ version: 1, project, proposals: [], draftChangeSets: [] })
        get().replaceProject(project)
        set((state) => ({ launcherOpen: false, hydrated: true, selectedRef: null, viewerMode: 'edit', activePlanStoreyRef: null, explodeStoreys: false,
          cameraRefocusRequest: state.cameraRefocusRequest + 1, toast: 'Furnished Zielonki loaded. Open House interior to explore both floors.' }))
      }
    } catch (error) { set({ toast: `Could not open Zielonki: ${error instanceof Error ? error.message : 'storage unavailable'}.` }) }
  },
  fitReferenceToZielonki: async () => {
    const state = get()
    const activeTarget = isZielonkiProject(state.project)
    const target = activeTarget ? { version: 1 as const, project: state.project, proposals: state.proposals, draftChangeSets: state.draftChangeSets }
      : await loadWorkspace(modernBarnProject.ref) ?? { version: 1 as const, project: ensureStarterOrchard(ensureStarterGarden(structuredClone(modernBarnProject))), proposals: [], draftChangeSets: [] }
    if (hasZielonkiInterior(target.project)) {
      get().restoreWorkspace(target, state.project.ref === target.project.ref ? { history: state.history, future: state.future } : await loadEditHistory(target.project))
      return
    }
    const reference = state.project.ref === REFERENCE_HOUSE_REF ? state.project : (await loadWorkspace(REFERENCE_HOUSE_REF))?.project ?? createReferenceHouse()
    const project = upgradeZielonkiPlacement(fitZielonkiInterior(target.project, reference))
    const blocking = validateProject(project).filter((issue) => issue.severity === 'error')
    if (blocking.length) throw new Error(blocking[0].message)
    // Save the previous model as a separate recoverable project before fitting the new house.
    const previous = structuredClone(target)
    previous.project.ref = `${target.project.ref}/before-interior-${Date.now()}`
    previous.project.name = `${target.project.name} · before interior fit`
    await saveWorkspace(previous)
    const workspace = { ...target, project, proposals: staleRecords(target.proposals, project.revision), draftChangeSets: staleDrafts(target.draftChangeSets, project.revision) }
    await saveWorkspace(workspace)
    get().restoreWorkspace(workspace)
    set({ future: [], history: [...(activeTarget ? state.history : []), structuredClone(target.project)].slice(-40), selectedRef: null, repositioningRef: null,
      hydrated: true, launcherOpen: false, viewerMode: 'edit', activePlanStoreyRef: null, explodeStoreys: false,
      cameraRefocusRequest: get().cameraRefocusRequest + 1, toast: 'Zielonki fitted to the measured interior. The previous house is saved in Projects.' })
  },
  openWorkspace: async (ref) => {
    assertWorkspaceEditable()
    await flushAutosave()
    try {
      if (ref === ARCHITEKT_REF) set({ projectSyncConflicts: await synchronizePublishedArchitekt() })
      if (ref === NOWE_MPZP_REF) set({ projectSyncConflicts: await synchronizePublishedNoweMpzp() })
      if (ref === NOWE_MPZP_V2_REF) set({ projectSyncConflicts: await synchronizePublishedNoweMpzpV2() })
      const saved = await loadWorkspace(ref)
      if (!saved) { set({ toast: `Saved project not found: ${ref}.` }); return }
      const project = isZielonkiProject(saved.project) ? ensureStarterOrchard(ensureStarterGarden(applyModernBarnPreset(saved.project))) : saved.project
      get().restoreWorkspace({ ...saved, project }, await loadEditHistory(project))
      set((state) => ({ launcherOpen: false, hydrated: true, cameraRefocusRequest: state.cameraRefocusRequest + 1 }))
    } catch (error) { set({ toast: `Saved project could not be opened: ${error instanceof Error ? error.message : 'storage unavailable'}.` }) }
  },
  renameWorkspace: async (ref, value) => {
    const name = normalizeWorkspaceName(value)
    const state = get()
    if (state.hydrated && state.project.ref === ref) {
      const previousName = state.project.name
      const project = { ...state.project, name }
      set({ project })
      try {
        await saveWorkspace({ version: 1, project, proposals: state.proposals, draftChangeSets: state.draftChangeSets }, { editHistory: { history: state.history, future: state.future } })
      } catch (error) {
        if (get().project.ref === ref && get().project.name === name) set({ project: { ...get().project, name: previousName } })
        throw error
      }
    } else await renameWorkspace(ref, name)
    set((current) => ({ savedWorkspaces: current.savedWorkspaces.map((item) => item.ref === ref ? { ...item, name } : item), toast: `Zapisano nazwę: ${name}.` }))
  },
  createVariant: (label, commands, metadata) => {
    const current = get().project
    const preview = applyCommands(current, commands)
    variantSequence += 1
    const createdAt = new Date().toISOString()
    const proposal: ProposalRecord = {
      ref: `variant/${slugify(label)}-r${current.revision}-${Date.now().toString(36)}-${variantSequence}`, label, baseRevision: current.revision,
      createdAt, commands: structuredClone(commands), project: preview, issues: validateProject(preview), metrics: calculateMetrics(preview), status: 'pending', ...metadata,
    }
    set((state) => ({ variants: [...state.variants, proposal], proposals: [...state.proposals, proposal], toast: `${label} is ready to review.` }))
    return proposal
  },
  applyVariant: (ref) => {
    const state = get(); const variant = state.variants.find((item) => item.ref === ref)
    if (!variant) throw new Error(`Variant not found: ${ref}`)
    if (variant.baseRevision !== state.project.revision) throw new Error('Variant is stale. Create it again from the current project.')
    if (variant.issues.some((issue) => issue.severity === 'error')) throw new Error('Variant contains blocking validation errors.')
    const next = { ...structuredClone(variant.project), name: state.project.name, revision: state.project.revision + 1, updatedAt: new Date().toISOString() }
    const decisionAt = new Date().toISOString()
    const proposals = state.proposals.map((proposal) => proposal.ref === ref ? { ...proposal, status: 'approved' as const, decisionAt, resultingRevision: next.revision } : proposal.status === 'pending' ? { ...proposal, status: 'stale' as const } : proposal)
    set({ project: next, future: [], history: [...state.history, structuredClone(state.project)].slice(-40), variants: [], proposals, draftChangeSets: staleDrafts(state.draftChangeSets, next.revision), confirmationVariantRef: null, repositioningRef: null, toast: `${variant.label} applied.` })
    return next
  },
  discardVariant: (ref, reason) => set((state) => ({
    variants: state.variants.filter((item) => item.ref !== ref),
    proposals: state.proposals.map((proposal) => proposal.ref === ref ? { ...proposal, status: 'rejected' as const, decisionAt: new Date().toISOString(), rejectionReason: reason } : proposal),
    confirmationVariantRef: state.confirmationVariantRef === ref ? null : state.confirmationVariantRef, toast: 'Proposal rejected and retained in history.',
  })),
  reopenProposal: (ref) => {
    const state = get(); const proposal = state.proposals.find((item) => item.ref === ref)
    if (!proposal) throw new Error(`Proposal not found: ${ref}`)
    if (proposal.status !== 'pending' || proposal.baseRevision !== state.project.revision) throw new Error('Only a current pending proposal can be reopened for approval.')
    if (!state.variants.some((item) => item.ref === ref)) set({ variants: [...state.variants, proposal] })
    set({ confirmationVariantRef: ref, selectedRef: commandFocusRef(proposal.commands), cameraRefocusRequest: state.cameraRefocusRequest + 1, toast: `${proposal.label} reopened for review.` })
    return proposal
  },
  recreateProposal: (ref) => {
    const proposal = get().proposals.find((item) => item.ref === ref)
    if (!proposal) throw new Error(`Proposal not found: ${ref}`)
    const recreated = get().createVariant(`${proposal.label} (recreated)`, proposal.commands, { recreatedFromRef: proposal.ref })
    get().reopenProposal(recreated.ref)
    return recreated
  },
  createDraftChangeSet: (ref, label, baseRevision) => {
    const state = get(); if (baseRevision !== state.project.revision) throw new Error(`Cannot create draft from revision ${baseRevision}; current revision is ${state.project.revision}.`)
    if (state.draftChangeSets.some((draft) => draft.ref === ref)) throw new Error(`Draft change set already exists: ${ref}`)
    const draft: DraftChangeSetModel = { ref, label, baseRevision, createdAt: new Date().toISOString(), commands: [], status: 'draft' }
    set({ draftChangeSets: [...state.draftChangeSets, draft] }); return draft
  },
  addDraftOperations: (ref, operations) => {
    const state = get(); const draft = state.draftChangeSets.find((item) => item.ref === ref)
    if (!draft) throw new Error(`Draft change set not found: ${ref}`)
    if (draft.status === 'stale' || draft.baseRevision !== state.project.revision) throw new Error(`Draft change set is stale: base revision ${draft.baseRevision}, current revision ${state.project.revision}.`)
    const commands = [...draft.commands, ...structuredClone(operations)]; const preview = applyCommands(state.project, commands); const updated = { ...draft, commands }
    set({ draftChangeSets: state.draftChangeSets.map((item) => item.ref === ref ? updated : item) }); return { draft: updated, preview }
  },
  finalizeDraftChangeSet: (ref) => {
    const state = get(); const draft = state.draftChangeSets.find((item) => item.ref === ref)
    if (!draft) throw new Error(`Draft change set not found: ${ref}`)
    if (draft.status === 'stale' || draft.baseRevision !== state.project.revision) throw new Error(`Draft change set is stale: base revision ${draft.baseRevision}, current revision ${state.project.revision}.`)
    if (!draft.commands.length) throw new Error('Cannot finalize an empty change set.')
    const variant = get().createVariant(draft.label, draft.commands, { sourceChangeSetRef: draft.ref })
    set({ draftChangeSets: get().draftChangeSets.filter((item) => item.ref !== ref) }); return variant
  },
  discardDraftChangeSet: (ref) => {
    const state = get(); if (!state.draftChangeSets.some((item) => item.ref === ref)) throw new Error(`Draft change set not found: ${ref}`)
    set({ draftChangeSets: state.draftChangeSets.filter((item) => item.ref !== ref), toast: 'Draft change set discarded.' })
  },
  commitCommand: (command) => {
    const state = get(); const next = applyCommand(state.project, command)
    const blocking = validateProject(next).filter((issue) => issue.severity === 'error')
    if (blocking.length) throw new Error(blocking[0].message)
    next.revision = state.project.revision + 1
    set({ project: next, future: [], history: [...state.history, structuredClone(state.project)].slice(-40), variants: [], proposals: staleRecords(state.proposals, next.revision), draftChangeSets: staleDrafts(state.draftChangeSets, next.revision), repositioningRef: null, toast: 'Spatial edit applied.' })
  },
  commitCommands: (commands, message = 'Spatial edits applied.') => {
    const state = get(); const next = applyCommands(state.project, commands)
    const blocking = validateProject(next).filter((issue) => issue.severity === 'error')
    if (blocking.length) throw new Error(blocking[0].message)
    next.revision = state.project.revision + 1
    next.updatedAt = new Date().toISOString()
    set({ project: next, future: [], history: [...state.history, structuredClone(state.project)].slice(-40), variants: [], proposals: staleRecords(state.proposals, next.revision), draftChangeSets: staleDrafts(state.draftChangeSets, next.revision), repositioningRef: null, toast: message })
    return next
  },
  undo: () => {
    const state = get(); const snapshot = state.history.at(-1)
    if (!snapshot) throw new Error('There is no committed change to undo.')
    const previous = { ...snapshot, name: state.project.name }
    set({ project: previous, history: state.history.slice(0, -1), future: [...state.future, structuredClone(state.project)].slice(-40), variants: [], proposals: state.proposals.map((proposal) => proposal.status === 'pending' ? { ...proposal, status: 'stale' as const } : proposal), draftChangeSets: state.draftChangeSets.map((draft) => ({ ...draft, status: 'stale' as const })), confirmationVariantRef: null, repositioningRef: null, toast: 'Last change undone.' })
    return previous
  },
  redo: () => {
    const state = get(); const snapshot = state.future.at(-1)
    if (!snapshot) throw new Error('There is no change to redo.')
    const next = { ...snapshot, name: state.project.name }
    set({ project: next, history: [...state.history, structuredClone(state.project)].slice(-40), future: state.future.slice(0, -1), variants: [], proposals: state.proposals.map((proposal) => proposal.status === 'pending' ? { ...proposal, status: 'stale' as const } : proposal), draftChangeSets: state.draftChangeSets.map((draft) => ({ ...draft, status: 'stale' as const })), confirmationVariantRef: null, repositioningRef: null, toast: 'Last change restored.' })
    return next
  },
})})

// A sync cannot begin halfway through a load/rename/migration already in progress.
const workspaceActions = useStudioStore.getState()
useStudioStore.setState({
  openLauncher: () => runWorkspaceActivity(() => workspaceActions.openLauncher()),
  openWorkspace: ref => runWorkspaceActivity(() => workspaceActions.openWorkspace(ref)),
  openReferenceHouse: () => runWorkspaceActivity(() => workspaceActions.openReferenceHouse()),
  openZielonkiStudy: () => runWorkspaceActivity(() => workspaceActions.openZielonkiStudy()),
  fitReferenceToZielonki: () => runWorkspaceActivity(() => workspaceActions.fitReferenceToZielonki()),
  renameWorkspace: (ref, name) => runWorkspaceActivity(() => workspaceActions.renameWorkspace(ref, name)),
})
